import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronDown } from "lucide-react";
import { Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";

interface SignupFormProps {
  onSwitchToLogin?: () => void;
  onSwitchToReset?: () => void;
}

export default function SignupForm({ onSwitchToLogin = () => {}, onSwitchToReset = () => {} }: SignupFormProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { signup, isAuthenticating, error } = useAuth();
  const { toast } = useToast();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const isMobile = useIsMobile();
  
  // Mobile viewport adjustment to prevent keyboard from hiding the form
  useEffect(() => {
    if (!isMobile) return;
    
    const handleViewportChange = () => {
      const visualViewport = window.visualViewport;
      if (!visualViewport) return;
      
      const viewportHeight = visualViewport.height;
      const windowHeight = window.innerHeight;
      const heightDiff = windowHeight - viewportHeight;
      
      if (heightDiff > 150) { // Keyboard is likely open
        document.body.style.transform = `translateY(-${Math.min(heightDiff / 3, 100)}px)`;
        document.body.style.position = 'relative';
      } else {
        document.body.style.transform = '';
        document.body.style.position = '';
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
        window.visualViewport.removeEventListener('scroll', handleViewportChange);
      }
      document.body.style.transform = '';
      document.body.style.position = '';
    };
  }, [isMobile]);
  
  // Show toast when there's an authentication error
  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Signup Error",
        description: error,
      });
    }
  }, [error, toast]);

  // Check if submit button is visible on mobile
  useEffect(() => {
    if (!isMobile) {
      setShowScrollButton(false);
      return;
    }

    const checkButtonVisibility = () => {
      const submitButton = document.querySelector('[type="submit"]');
      if (!submitButton) return;

      const rect = submitButton.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      
      // Show scroll button if submit button is below the viewport
      setShowScrollButton(rect.bottom > viewportHeight - 50);
    };

    checkButtonVisibility();
    
    const observer = new ResizeObserver(checkButtonVisibility);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', checkButtonVisibility);
      observer.observe(document.body);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', checkButtonVisibility);
      }
      observer.disconnect();
    };
  }, [isMobile, password]); // Re-check when password changes (requirements show/hide)

  const scrollToSubmit = () => {
    const submitButton = document.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
      requirements: {
        minLength,
        hasUpperCase,
        hasLowerCase,
        hasNumbers,
        hasSpecialChar,
      },
    };
  };

  const passwordValidation = validatePassword(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please enter your first and last name.",
      });
      return;
    }

    if (!passwordValidation.isValid) {
      toast({
        variant: "destructive",
        title: "Password Requirements Not Met",
        description: "Please ensure your password meets all security requirements.",
      });
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Please make sure your passwords match.",
      });
      return;
    }
    
    try {
      await signup(email, password, firstName, lastName);
    } catch (error) {
      // Error is handled by the AuthContext and shown via useEffect
    }
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        throw error;
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Google Sign Up Failed",
        description: error.message,
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleForgotPassword = () => {
    onSwitchToReset();
  };

  return (
    <div className="w-full">
      <Alert className="mb-3 sm:mb-4 border-orange-200 bg-orange-50 p-3">
        <Lock className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
        <AlertDescription className="text-xs sm:text-sm text-orange-800 ml-2">
          <strong>🔒 Privacy Note</strong><br />
          By using Shared Matter, you agree that your saved "memories" (messages, notes, and other data) are currently stored as plain text in an encrypted database. We're actively working on adding end-to-end encryption, but for now, please avoid storing sensitive personal or financial information.
        </AlertDescription>
      </Alert>

      <div className="space-y-3 sm:space-y-4">
        <Button
          type="button"
          variant="outline"
          className="w-full h-10 sm:h-11"
          onClick={handleGoogleSignUp}
          disabled={isGoogleLoading}
        >
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {isGoogleLoading ? "Creating account..." : "Continue with Google"}
        </Button>
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or continue with email
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-sm">First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="h-9 sm:h-10 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-sm">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="h-9 sm:h-10 text-sm"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-9 sm:h-10 text-sm"
            />
            {password && (
              <div className="text-xs space-y-1 mt-2 max-h-20 overflow-y-auto">
                <div className="grid grid-cols-1 gap-1">
                  <div className={`flex items-center gap-2 ${passwordValidation.requirements.minLength ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="flex-shrink-0">{passwordValidation.requirements.minLength ? '✓' : '✗'}</span>
                    <span>8+ chars</span>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <div className={`flex items-center gap-1 ${passwordValidation.requirements.hasUpperCase ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="flex-shrink-0">{passwordValidation.requirements.hasUpperCase ? '✓' : '✗'}</span>
                      <span>Upper</span>
                    </div>
                    <div className={`flex items-center gap-1 ${passwordValidation.requirements.hasLowerCase ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="flex-shrink-0">{passwordValidation.requirements.hasLowerCase ? '✓' : '✗'}</span>
                      <span>Lower</span>
                    </div>
                    <div className={`flex items-center gap-1 ${passwordValidation.requirements.hasNumbers ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="flex-shrink-0">{passwordValidation.requirements.hasNumbers ? '✓' : '✗'}</span>
                      <span>Number</span>
                    </div>
                    <div className={`flex items-center gap-1 ${passwordValidation.requirements.hasSpecialChar ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="flex-shrink-0">{passwordValidation.requirements.hasSpecialChar ? '✓' : '✗'}</span>
                      <span>Special</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-10 sm:h-11 text-sm sm:text-base" 
            disabled={isAuthenticating || !passwordValidation.isValid}
          >
            {isAuthenticating ? "Creating account..." : "Create account"}
          </Button>
        </form>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 mt-4 sm:mt-6">
          <div className="text-xs sm:text-sm text-muted-foreground">
            Already have an account?{" "}
            <Button variant="link" className="p-0 h-auto text-xs sm:text-sm" onClick={onSwitchToLogin}>
              Sign in
            </Button>
          </div>
          <Button 
            variant="link" 
            className="p-0 h-auto text-xs sm:text-sm text-blue-600" 
            onClick={handleForgotPassword}
          >
            Forgot password?
          </Button>
        </div>
      </div>
      
      {/* Scroll button for mobile when submit button is not visible */}
      {showScrollButton && (
        <Button
          type="button"
          size="sm"
          onClick={scrollToSubmit}
          className="fixed bottom-4 right-4 z-50 rounded-full shadow-lg bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
